/**
 * The one place an asset write says which storage shape it wants
 * (CONTENT_MODEL_SPEC §6.1; TraceBackend#930).
 *
 * `POST /assets` and `PATCH /assets/{assetId}` take an optional multipart
 * `contentStorage` field: `Folder` stores the upload as an asset folder with a
 * generated wrapper document and strips `metadata.behaviors`, `Legacy` stores
 * the leaf or bundle raw in `trace-cms-assets` and keeps them. Omitting the
 * field inherits whatever `ContentOptions:LegacyWrites` an environment happens
 * to be on that week — which is exactly the thing a build must not depend on,
 * so every request this editor makes says what it wants.
 *
 * THE DOCUMENT EXCEPTION: a `.gltf` upload is a DOCUMENT, and a document can
 * only be a folder — its `files[]` name blobs stored beside it. The server
 * takes the folder path for one on every environment, and answers `Legacy` +
 * document with a **400** (asking for the impossible combination explicitly).
 * So a `.gltf` is always sent as `Folder`, in either mode. That covers "Save
 * as asset" (promotion, §3.5) and the library-asset PATCH (§2.6), both of
 * which send a document and neither of which has a legacy spelling.
 *
 * Everything else — a `.trace`/`.zip` scene bundle, a dropped `.glb`, an
 * image, a video — follows the build's switch.
 */

import { CONTENT_STORAGE_API_VALUE, type ContentStorageApiValue } from '../config/contentStorage';

/**
 * The `contentStorage` field, as an extension of the generated CMS client's
 * request types.
 *
 * TODO(tv regen): contentStorage typed once trace-viewer regenerates its CMS
 * client. `trace-viewer/src/cms/api/Assets.ts` and `data-contracts.ts` are
 * generated by swagger-typescript-api from the backend's swagger and do not
 * carry the field yet, so it is declared here and intersected onto the
 * request shape at the call. It *does* reach the wire without any further
 * help: the generated `createFormData` reduces over `Object.keys(input)` and
 * appends a part for every own key, so an extra key is an extra multipart
 * field (pinned by `test/cms/asset-writes.test.ts`). When the regen lands,
 * delete this type and use the generated one.
 */
export interface AssetContentStorageField {
    contentStorage?: ContentStorageApiValue;
}

/** A `.gltf` upload — a DOCUMENT, which can only ever be a folder. */
function isDocumentUpload(file: unknown): boolean {
    return file instanceof File && file.name.toLowerCase().endsWith('.gltf');
}

/**
 * The storage mode a given upload should declare: `Folder` for a `.gltf`
 * document whatever the build is set to, otherwise the build's switch.
 */
export function contentStorageFor(file: unknown): ContentStorageApiValue {
    return isDocumentUpload(file) ? 'Folder' : CONTENT_STORAGE_API_VALUE;
}

/**
 * Stamp the build's content write mode onto an asset write's multipart parts.
 *
 * Returns a NEW object (the caller's parts are never mutated), and an explicit
 * `contentStorage` a caller already set wins — a caller that knows better than
 * the build (a migration tool, a test) keeps its choice.
 */
export function withContentStorage<T extends { File?: File | null }>(
    parts: T & AssetContentStorageField,
): T & AssetContentStorageField {
    if (parts.contentStorage) return parts;
    return { ...parts, contentStorage: contentStorageFor(parts.File) };
}
